/**
 * AuditLogButton — 2026-02-13 (user request).
 * Tombol reusable "Audit Log" untuk dipasang di header halaman fitur apapun.
 * Membuka dialog berisi entri audit dari collection `audit_logs` yang cocok
 * dengan `feature_key` (+ opsional `target_id`), sudah difilter di backend
 * sesuai scope viewer (hierarki level + override).
 *
 * Contoh pemakaian:
 *   <AuditLogButton feature_key="project" target_id={projectId} title={project.name} />
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileClock, RefreshCw, X } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";

// 2026-09-25: auto-hide bila viewer tidak punya akses audit log.
// Backend gate: BOD Lv1 || audit_log_access.view || personnel_manage.view.
function useCanViewAudit() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  if (!user) return false;
  if (user.division === "BOD" && Number(user.level) === 1) return true;
  return canPerm(user, "audit_log_access", "view") || canPerm(user, "personnel_manage", "view");
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtValue(v) {
  if (v == null) return "-";
  if (typeof v === "object") {
    return Object.entries(v).map(([k, val]) => `${k}: ${k.endsWith("value") && typeof val === "number" ? formatIDR(val) : String(val)}`).join(" · ");
  }
  if (typeof v === "number") return v.toLocaleString("id-ID");
  return String(v);
}

export default function AuditLogButton({ feature_key, target_id, title, labelSuffix, className = "", size = "md" }) {
  const [open, setOpen] = useState(false);
  const canView = useCanViewAudit();
  if (!canView) return null;
  const sizeCls = size === "sm" ? "px-1.5 py-1 text-[9px]" : "px-2.5 py-1.5 text-[10px]";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        data-testid={`audit-btn-${feature_key}${target_id ? `-${target_id}` : ""}`}
        title={`Lihat riwayat perubahan${title ? ` — ${title}` : ""}`}
        className={`inline-flex items-center gap-1 border border-slate-300 text-slate-600 hover:text-[#0B1D3A] hover:border-[#0B1D3A] uppercase tracking-wider font-semibold ${sizeCls} ${className}`}>
        <FileClock size={size === "sm" ? 10 : 12} /> Audit{labelSuffix ? ` · ${labelSuffix}` : size === "sm" ? "" : " Log"}
      </button>
      {open && <AuditLogDialog feature_key={feature_key} target_id={target_id} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}

function AuditLogDialog({ feature_key, target_id, title, onClose }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState("");
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (feature_key) params.feature_key = feature_key;
      if (target_id) params.target_id = target_id;
      const { data } = await api.get("/audit-logs", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setScope(data.scope || "");
    } catch (err) {
      const msg = formatDetail(err?.response?.data?.detail) || "Gagal memuat audit log.";
      toast.error(msg);
      if (err?.response?.status === 403) onClose();
    } finally { setLoading(false); }
  }, [feature_key, target_id, page, onClose]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="audit-dialog">
      <div className="bg-white border border-slate-200 shadow-2xl w-full max-w-5xl mt-8">
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div>
            <p className="overline text-[9px] text-slate-500">Audit Log · {feature_key}</p>
            <h2 className="font-display text-lg text-[#0B1D3A]">{title || "Riwayat Perubahan"}</h2>
            {scope && <p className="text-[10px] text-slate-500 mt-0.5" data-testid="audit-dialog-scope">Cakupan Anda: {scope}</p>}
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={load} disabled={loading} data-testid="audit-dialog-refresh"
              className="p-1.5 border border-slate-300 hover:bg-slate-100" title="Muat ulang">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
            <button type="button" onClick={onClose} data-testid="audit-dialog-close"
              className="p-1.5 border border-slate-300 hover:bg-slate-100" title="Tutup">
              <X size={13} />
            </button>
          </div>
        </div>
        <div className="p-3 max-h-[70vh] overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Waktu</th>
                <th className="px-2 py-1.5 text-left">Aksi</th>
                <th className="px-2 py-1.5 text-left">Aktor</th>
                <th className="px-2 py-1.5 text-left">Divisi/Lv</th>
                <th className="px-2 py-1.5 text-left">Field</th>
                <th className="px-2 py-1.5 text-left">Nilai Lama → Baru</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-6 text-slate-400 italic">Memuat…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-slate-400 italic" data-testid="audit-dialog-empty">Belum ada entri audit yang cocok.</td></tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-dialog-row-${i}`}>
                  <td className="px-2 py-1.5 tabular-nums whitespace-nowrap text-slate-700">{fmtDateTime(r.created_at || r.at)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold border bg-slate-100 text-slate-700 border-slate-300">{r.action || "-"}</span>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-medium text-[#0B1D3A]">{r.actor_name || "-"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-[10px] text-slate-500 uppercase tracking-wider">
                    {r.actor_division || "-"}{r.actor_level ? ` · Lv${r.actor_level}` : ""}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 font-mono text-[10px] whitespace-nowrap">{r.field || "-"}</td>
                  <td className="px-2 py-1.5 text-[10px]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {r.old_value != null && <span className="text-slate-500 line-through">{fmtValue(r.old_value)}</span>}
                      {r.old_value != null && r.new_value != null && <span className="text-slate-400">→</span>}
                      {r.new_value != null && <span className="font-semibold text-[#0B1D3A]">{fmtValue(r.new_value)}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-600 tabular-nums" data-testid="audit-dialog-pagination">
          <span>{total} entri</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}
              className="px-2 py-1 border border-slate-300 hover:bg-slate-100 disabled:opacity-40">← Sebelumnya</button>
            <span>Hal {page} / {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}
              className="px-2 py-1 border border-slate-300 hover:bg-slate-100 disabled:opacity-40">Berikutnya →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
